import type { FieldValues } from '../gallery/fieldTypes'
import { twColorToHex } from './twColor'

type CodeGenerator = (v: FieldValues) => string

const generators: Record<string, CodeGenerator> = {
  'button-hover': (v) => {
    const hoverScale = v.hoverScale ?? 1.05
    const tapScale = v.tapScale ?? 0.97
    const stiffness = v.stiffness ?? 400
    const damping = v.damping ?? 17
    const bgColor = v.bgColor ?? 'bg-indigo-600'

    return `import { motion } from "motion/react"

export function ButtonHover() {
  return (
    <motion.button
      whileHover={{ scale: ${hoverScale} }}
      whileTap={{ scale: ${tapScale} }}
      transition={{ type: "spring", stiffness: ${stiffness}, damping: ${damping} }}
      className="${bgColor} px-6 py-3 text-white text-sm font-semibold rounded-lg shadow-md"
    >
      Hover Me
    </motion.button>
  )
}`
  },

  'toggle-switch': (v) => {
    const stiffness = v.stiffness ?? 500
    const damping = v.damping ?? 30
    const onColor = v.onColor ?? 'bg-green-500'
    const offColor = v.offColor ?? 'bg-gray-300'
    const knobSize = v.knobSize ?? 28

    return `import { useState } from "react"
import { motion } from "motion/react"

export function ToggleSwitch() {
  const [isOn, setIsOn] = useState(false)

  return (
    <button
      onClick={() => setIsOn(!isOn)}
      className={\`w-16 h-9 rounded-full p-1 flex cursor-pointer transition-colors \${
        isOn ? "${onColor} justify-end" : "${offColor} justify-start"
      }\`}
    >
      <motion.div
        layout
        transition={{ type: "spring", stiffness: ${stiffness}, damping: ${damping} }}
        className="bg-white rounded-full shadow-md"
        style={{ width: ${knobSize}, height: ${knobSize} }}
      />
    </button>
  )
}`
  },

  'slide-fade': (v) => {
    const initialX = v.initialX ?? -60
    const initialOpacity = v.initialOpacity ?? 0
    const stiffness = v.stiffness ?? 260
    const damping = v.damping ?? 20

    return `import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "motion/react"

export function SlideFade() {
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    const interval = setInterval(() => setVisible((v) => !v), 2000)
    return () => clearInterval(interval)
  }, [])

  return (
    <AnimatePresence mode="wait">
      {visible && (
        <motion.div
          key="slide"
          initial={{ x: ${initialX}, opacity: ${initialOpacity} }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: ${-initialX}, opacity: ${initialOpacity} }}
          transition={{ type: "spring", stiffness: ${stiffness}, damping: ${damping} }}
          className="w-32 h-20 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl shadow-lg"
        />
      )}
    </AnimatePresence>
  )
}`
  },

  'photo-flip': (v) => {
    const duration = v.duration ?? 0.6
    const perspective = v.perspective ?? 800
    const useSpring = v.useSpring ?? false
    const stiffness = v.stiffness ?? 200
    const damping = v.damping ?? 25

    const transitionStr = useSpring
      ? `{ type: "spring", stiffness: ${stiffness}, damping: ${damping} }`
      : `{ duration: ${duration}, ease: "easeInOut" }`

    return `import { useState, useEffect } from "react"
import { motion } from "motion/react"

const PHOTO_URL = "https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=400&h=300&fit=crop&q=80"

export function PhotoFlip() {
  const [flipped, setFlipped] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => setFlipped((f) => !f), 3000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div style={{ perspective: ${perspective} }} onClick={() => setFlipped((f) => !f)} className="cursor-pointer">
      <motion.div
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={${transitionStr}}
        style={{ transformStyle: "preserve-3d" }}
        className="relative w-56 h-40 rounded-xl"
      >
        <div
          className="absolute inset-0 rounded-xl overflow-hidden shadow-xl"
          style={{ backfaceVisibility: "hidden" }}
        >
          <img src={PHOTO_URL} alt="Mountain landscape" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <span className="absolute bottom-2 left-3 text-white text-xs font-semibold drop-shadow">
            Click to flip
          </span>
        </div>
        <div
          className="absolute inset-0 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 shadow-xl flex flex-col items-center justify-center gap-2 p-4"
          style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
        >
          <span className="text-white text-sm font-semibold">Mountain Vista</span>
          <span className="text-white/50 text-[10px] text-center">Photo from Unsplash</span>
        </div>
      </motion.div>
    </div>
  )
}`
  },

  'crossfade-scale': (v) => {
    const initialScale = v.initialScale ?? 0.9
    const exitScale = v.exitScale ?? 1.05
    const duration = v.duration ?? 0.4
    const ease = v.ease ?? 'easeInOut'

    return `import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "motion/react"

const pages = [
  { key: "a", label: "Dashboard", color: "from-sky-500 to-blue-600" },
  { key: "b", label: "Settings", color: "from-amber-500 to-orange-600" },
]

export function CrossfadeScale() {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => setIndex((i) => (i + 1) % pages.length), 2500)
    return () => clearInterval(interval)
  }, [])

  const page = pages[index]

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={page.key}
        initial={{ opacity: 0, scale: ${initialScale} }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: ${exitScale} }}
        transition={{ duration: ${duration}, ease: "${ease}" }}
        className={\`w-32 h-20 bg-gradient-to-br \${page.color} rounded-xl shadow-lg\`}
      />
    </AnimatePresence>
  )
}`
  },

  'reveal-on-scroll': (v) => {
    const initialY = v.initialY ?? 30
    const initialOpacity = v.initialOpacity ?? 0
    const duration = v.duration ?? 0.5
    const staggerDelay = v.staggerDelay ?? 0.1
    const itemCount = v.itemCount ?? 8

    return `import { useRef } from "react"
import { motion, useInView } from "motion/react"

function RevealCard({ index }: { index: number }) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: false, margin: "-20px" })

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: ${initialOpacity}, y: ${initialY} }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: ${initialOpacity}, y: ${initialY} }}
      transition={{ duration: ${duration}, delay: index * ${staggerDelay} }}
      className="h-16 bg-white rounded-lg shadow-sm border border-gray-100"
    />
  )
}

export function RevealOnScroll() {
  return (
    <div className="space-y-3 overflow-y-auto">
      {Array.from({ length: ${itemCount} }, (_, i) => (
        <RevealCard key={i} index={i} />
      ))}
    </div>
  )
}`
  },

  'parallax-layers': (v) => {
    const depth1 = v.depth1 ?? 15
    const depth2 = v.depth2 ?? 30
    const depth3 = v.depth3 ?? 50
    const smoothing = v.smoothing ?? 100

    return `import { useRef } from "react"
import { motion, useMotionValue, useSpring, useTransform } from "motion/react"

export function ParallaxLayers() {
  const containerRef = useRef<HTMLDivElement>(null)
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)

  const springConfig = { stiffness: ${smoothing}, damping: 20 }
  const smoothX = useSpring(mouseX, springConfig)
  const smoothY = useSpring(mouseY, springConfig)

  const x1 = useTransform(smoothX, [-1, 1], [-${depth1}, ${depth1}])
  const y1 = useTransform(smoothY, [-1, 1], [-${depth1}, ${depth1}])
  const x2 = useTransform(smoothX, [-1, 1], [-${depth2}, ${depth2}])
  const y2 = useTransform(smoothY, [-1, 1], [-${depth2}, ${depth2}])
  const x3 = useTransform(smoothX, [-1, 1], [-${depth3}, ${depth3}])
  const y3 = useTransform(smoothY, [-1, 1], [-${depth3}, ${depth3}])

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return
    mouseX.set(((e.clientX - rect.left) / rect.width - 0.5) * 2)
    mouseY.set(((e.clientY - rect.top) / rect.height - 0.5) * 2)
  }

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => { mouseX.set(0); mouseY.set(0) }}
      className="h-full flex items-center justify-center relative overflow-hidden cursor-crosshair"
    >
      <div className="relative w-48 h-48">
        <motion.div style={{ x: x1, y: y1 }} className="absolute inset-0 bg-sky-200/50 rounded-2xl" />
        <motion.div style={{ x: x2, y: y2 }} className="absolute inset-3 bg-indigo-300/50 rounded-2xl" />
        <motion.div style={{ x: x3, y: y3 }} className="absolute inset-6 bg-violet-400/60 rounded-2xl" />
      </div>
    </div>
  )
}`
  },

  'skeleton-shimmer': (v) => {
    const duration = v.duration ?? 1.5
    const delay1 = v.delay1 ?? 0.2
    const delay2 = v.delay2 ?? 0.4
    const delay3 = v.delay3 ?? 0.6

    return `import { motion } from "motion/react"

function ShimmerBar({ delay, className }: { delay: number; className: string }) {
  return (
    <div className={\`relative overflow-hidden \${className}\`}>
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
        animate={{ x: ["-100%", "100%"] }}
        transition={{ duration: ${duration}, repeat: Infinity, ease: "linear", delay }}
      />
    </div>
  )
}

export function SkeletonShimmer() {
  return (
    <div className="w-full max-w-[200px] bg-white rounded-xl shadow-sm border overflow-hidden">
      <ShimmerBar delay={0} className="h-20 bg-gray-200" />
      <div className="p-3 space-y-2">
        <ShimmerBar delay={${delay1}} className="h-3 bg-gray-200 rounded w-3/4" />
        <ShimmerBar delay={${delay2}} className="h-2.5 bg-gray-100 rounded w-full" />
        <ShimmerBar delay={${delay3}} className="h-2.5 bg-gray-100 rounded w-1/2" />
      </div>
    </div>
  )
}`
  },

  'success-checkmark': (v) => {
    const circleDuration = v.circleDuration ?? 0.6
    const checkDelay = v.checkDelay ?? 0.5
    const stiffness = v.stiffness ?? 200
    const damping = v.damping ?? 15
    const color = twColorToHex(v.color ?? 'bg-green-500')
    const strokeWidth = v.strokeWidth ?? 3

    return `import { useState, useEffect } from "react"
import { motion } from "motion/react"

export function SuccessCheckmark() {
  const [key, setKey] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => setKey((k) => k + 1), 3000)
    return () => clearInterval(interval)
  }, [])

  return (
    <motion.svg key={key} width="64" height="64" viewBox="0 0 64 64" fill="none">
      <motion.circle
        cx="32" cy="32" r="28"
        stroke="${color}" strokeWidth={${strokeWidth}} fill="none"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: ${circleDuration}, ease: "easeOut" }}
      />
      <motion.path
        d="M20 33 L28 41 L44 25"
        stroke="${color}" strokeWidth={${strokeWidth}}
        strokeLinecap="round" strokeLinejoin="round" fill="none"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.4, delay: ${checkDelay}, type: "spring", stiffness: ${stiffness}, damping: ${damping} }}
      />
    </motion.svg>
  )
}`
  },

  'expandable-card': (v) => {
    const stiffness = v.stiffness ?? 300
    const damping = v.damping ?? 30
    const contentDuration = v.contentDuration ?? 0.3

    return `import { useState } from "react"
import { motion, AnimatePresence } from "motion/react"

export function ExpandableCard() {
  const [expanded, setExpanded] = useState(false)

  return (
    <motion.div
      layout
      onClick={() => setExpanded(!expanded)}
      className="bg-white rounded-xl shadow-sm border overflow-hidden cursor-pointer w-full max-w-[200px]"
      transition={{ layout: { type: "spring", stiffness: ${stiffness}, damping: ${damping} } }}
    >
      <motion.div layout="position" className="p-3">
        <h3 className="text-xs font-semibold">Card Title</h3>
        <p className="text-[10px] text-gray-500">Click to {expanded ? "collapse" : "expand"}</p>
      </motion.div>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: ${contentDuration} }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 border-t pt-2">
              <p className="text-[10px] text-gray-400">
                Expanded content with layout animation.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}`
  },

  'tab-transition': (v) => {
    const stiffness = v.stiffness ?? 400
    const damping = v.damping ?? 30
    const fadeDuration = v.fadeDuration ?? 0.2
    const yOffset = v.yOffset ?? 8

    return `import { useState } from "react"
import { motion } from "motion/react"

const tabs = ["Home", "About", "Blog"]

export function TabTransition() {
  const [active, setActive] = useState(0)

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {tabs.map((tab, i) => (
          <button key={tab} onClick={() => setActive(i)} className="relative px-4 py-1.5 text-xs font-medium z-10">
            {active === i && (
              <motion.div
                layoutId="tab-indicator"
                className="absolute inset-0 bg-white rounded-md shadow-sm"
                transition={{ type: "spring", stiffness: ${stiffness}, damping: ${damping} }}
              />
            )}
            <span className={\`relative z-10 \${active === i ? "text-gray-900" : "text-gray-500"}\`}>
              {tab}
            </span>
          </button>
        ))}
      </div>
      <motion.div
        key={active}
        initial={{ opacity: 0, y: ${yOffset} }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ${fadeDuration} }}
        className="text-sm text-gray-400"
      >
        {tabs[active]} content
      </motion.div>
    </div>
  )
}`
  },

  'cascading-grid': (v) => {
    const staggerDelay = v.staggerDelay ?? 0.06
    const initialScale = v.initialScale ?? 0.8
    const initialY = v.initialY ?? 10
    const columns = v.columns ?? 4
    const gap = v.gap ?? 8

    return `import { useState, useEffect } from "react"
import { motion } from "motion/react"

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: ${staggerDelay} } },
}

const item = {
  hidden: { opacity: 0, scale: ${initialScale}, y: ${initialY} },
  show: { opacity: 1, scale: 1, y: 0 },
}

const colors = [
  "bg-rose-400", "bg-orange-400", "bg-amber-400", "bg-lime-400",
  "bg-emerald-400", "bg-teal-400", "bg-cyan-400", "bg-sky-400",
  "bg-blue-400", "bg-indigo-400", "bg-violet-400", "bg-purple-400",
]

export function CascadingGrid() {
  const [key, setKey] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => setKey((k) => k + 1), 3500)
    return () => clearInterval(interval)
  }, [])

  return (
    <motion.div
      key={key}
      variants={container}
      initial="hidden"
      animate="show"
      className="grid"
      style={{ gridTemplateColumns: "repeat(${columns}, 1fr)", gap: ${gap} }}
    >
      {colors.map((color, i) => (
        <motion.div key={i} variants={item} className={\`w-8 h-8 rounded-lg \${color}\`} />
      ))}
    </motion.div>
  )
}`
  },

  'text-letter-by-letter': (v) => {
    const text = v.text ?? 'Nimble Labs'
    const staggerDelay = v.staggerDelay ?? 0.05
    const stiffness = v.stiffness ?? 300
    const damping = v.damping ?? 20
    const initialY = v.initialY ?? 20

    return `import { useState, useEffect } from "react"
import { motion } from "motion/react"

const text = "${text}"

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: ${staggerDelay} } },
}

const letter = {
  hidden: { opacity: 0, y: ${initialY} },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: ${stiffness}, damping: ${damping} } },
}

export function TextLetterByLetter() {
  const [key, setKey] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => setKey((k) => k + 1), 3000)
    return () => clearInterval(interval)
  }, [])

  return (
    <motion.div key={key} variants={container} initial="hidden" animate="show" className="flex">
      {text.split("").map((char, i) => (
        <motion.span key={i} variants={letter} className="text-lg font-bold text-gray-800">
          {char === " " ? "\\u00A0" : char}
        </motion.span>
      ))}
    </motion.div>
  )
}`
  },

  'line-drawing': (v) => {
    const strokeColor = twColorToHex(v.strokeColor ?? 'bg-indigo-500')
    const strokeWidth = v.strokeWidth ?? 2.5
    const duration = v.duration ?? 2
    const circleColor = twColorToHex(v.circleColor ?? 'bg-purple-500')
    const circleDelay = v.circleDelay ?? 0.8

    return `import { useState, useEffect } from "react"
import { motion } from "motion/react"

export function LineDrawing() {
  const [key, setKey] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => setKey((k) => k + 1), 4000)
    return () => clearInterval(interval)
  }, [])

  return (
    <motion.svg key={key} width="80" height="80" viewBox="0 0 80 80" fill="none">
      <motion.polygon
        points="40,8 72,68 8,68"
        stroke="${strokeColor}" strokeWidth={${strokeWidth}} strokeLinejoin="round" fill="none"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: ${duration}, ease: "easeInOut" }}
      />
      <motion.circle
        cx="40" cy="48" r="12"
        stroke="${circleColor}" strokeWidth={${Math.max(strokeWidth - 0.5, 1)}} fill="none"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: ${(duration * 0.75).toFixed(2)}, delay: ${circleDelay}, ease: "easeInOut" }}
      />
    </motion.svg>
  )
}`
  },

  'shape-morph': (v) => {
    const rotate = v.rotate ?? 180
    const scale = v.scale ?? 1.1
    const stiffness = v.stiffness ?? 200
    const damping = v.damping ?? 20
    const borderRadiusFrom = v.borderRadiusFrom ?? 12
    const borderRadiusTo = v.borderRadiusTo ?? 50

    return `import { useState, useEffect } from "react"
import { motion } from "motion/react"

export function ShapeMorph() {
  const [isCircle, setIsCircle] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => setIsCircle((v) => !v), 2000)
    return () => clearInterval(interval)
  }, [])

  return (
    <motion.div
      animate={{
        borderRadius: isCircle ? "${borderRadiusTo}%" : "${borderRadiusFrom}px",
        rotate: isCircle ? ${rotate} : 0,
        scale: isCircle ? ${scale} : 1,
      }}
      transition={{ type: "spring", stiffness: ${stiffness}, damping: ${damping} }}
      className="w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-600 shadow-lg"
    />
  )
}`
  },

  'draggable-spring-back': (v) => {
    const dragElastic = v.dragElastic ?? 0.6
    const dragScale = v.dragScale ?? 1.1
    const stiffness = v.stiffness ?? 300
    const damping = v.damping ?? 20

    return `import { motion } from "motion/react"

export function DraggableSpringBack() {
  return (
    <motion.div
      drag
      dragConstraints={{ top: 0, bottom: 0, left: 0, right: 0 }}
      dragElastic={${dragElastic}}
      whileDrag={{ scale: ${dragScale} }}
      transition={{ type: "spring", stiffness: ${stiffness}, damping: ${damping} }}
      className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl shadow-lg cursor-grab active:cursor-grabbing"
    >
      <span className="text-white text-[10px] font-semibold pointer-events-none">Drag</span>
    </motion.div>
  )
}`
  },

  'swipe-to-dismiss': (v) => {
    const swipeThreshold = v.swipeThreshold ?? 80
    const initialX = v.initialX ?? -40
    const exitX = v.exitX ?? 200
    const dragScale = v.dragScale ?? 1.02

    return `import { useState } from "react"
import { motion, AnimatePresence } from "motion/react"

export function SwipeToDismiss() {
  const [items, setItems] = useState([1, 2, 3])
  const [nextId, setNextId] = useState(4)

  const handleDragEnd = (id: number, info: { offset: { x: number } }) => {
    if (Math.abs(info.offset.x) > ${swipeThreshold}) {
      setItems((prev) => prev.filter((i) => i !== id))
      setTimeout(() => {
        setNextId((n) => { setItems((prev) => [...prev, n]); return n + 1 })
      }, 400)
    }
  }

  return (
    <AnimatePresence mode="popLayout">
      {items.map((id) => (
        <motion.div
          key={id} layout drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          onDragEnd={(_, info) => handleDragEnd(id, info)}
          initial={{ opacity: 0, x: ${initialX} }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: ${exitX}, transition: { duration: 0.2 } }}
          whileDrag={{ scale: ${dragScale} }}
          className="w-full max-w-[180px] h-10 bg-white rounded-lg shadow-sm border cursor-grab active:cursor-grabbing"
        />
      ))}
    </AnimatePresence>
  )
}`
  },
}

export function generateExampleCode(exampleId: string, values: FieldValues): string {
  const generator = generators[exampleId]
  if (!generator) return `// Code generation not available for this example`
  return generator(values)
}
